import { useState } from 'react';
import { dismiss, notifyProblems } from '../lib/errorNotices';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createOrganisationSchema } from '@promanage/contracts';
import {
  DEFAULT_INITIAL_ADMIN_ROLES,
  assignableRoles,
  canAdministerPlatform,
  organisationSetupIssues,
} from '@promanage/core';
import { Button, Field, Input, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { SettingsNav } from '../features/settings/SettingsNav';
import { RolePicker } from '../features/users/RolePicker';
import { useSession } from '../features/auth/session';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/settings/organisations/new')({
  component: NewOrganisationPage,
});

type Created = RouterOutputs['platform']['createOrganisation'];

/**
 * Opening a new customer.
 *
 * THIS PAGE GIVES US NOTHING. It creates the agency, its four system ledger accounts and its
 * first administrator, and hands the operator a one-time link to pass on — and no membership
 * for themselves. The agency is, from its first second, a place ProManage cannot read: getting
 * in afterwards is Request access on the list behind this, with a reason the customer reads in
 * their own audit log. The alternative shape — making ourselves a member and remembering to
 * leave — is the one this deliberately does not implement, because it depends on remembering.
 *
 * WHAT IT DELIBERATELY DOES NOT ASK FOR. The trust account, the GST registration, the
 * management fee, the approval thresholds. Every one of those is a fact about somebody else's
 * business that the agency states on its own settings screens, under its own roles, in its own
 * audit log. A trust account number in particular is docs/SECURITY.md §5's whole subject — a
 * bank account typed in by us from an email is where that fraud begins.
 */

/** One notice for this form's own checks, replaced on the next attempt. */
const SETUP_PROBLEM = 'setup-problem';
function NewOrganisationPage() {
  const session = useSession();

  if (!canAdministerPlatform(session?.roles ?? [])) {
    // Tidiness, not a control — the procedure refuses regardless. See features/auth/gate.ts.
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="New agency" subtitle="Platform administration" />
        <SettingsNav />
        <Panel className="p-6">
          <p className="text-[0.875rem] text-ink-muted">
            Opening a new agency is ProManage platform administration.
          </p>
        </Panel>
      </div>
    );
  }

  return <SetupForm />;
}

function SetupForm() {
  const client = useQueryClient();
  const unsaved = useUnsavedChanges();

  const [name, setName] = useState('');
  const [nzbn, setNzbn] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([...DEFAULT_INITIAL_ADMIN_ROLES]);

  const create = useMutation(
    trpc.platform.createOrganisation.mutationOptions({
      meta: { errorTitle: 'Could not set that agency up' },
      onSuccess() {
        unsaved.saved();
        void client.invalidateQueries({ queryKey: trpc.platform.listOrganisations.queryKey() });
      },
    }),
  );

  if (create.data) return <SetupResult created={create.data} />;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    // The same rules the API runs and the database restates, run here for a faster answer —
    // never instead of them. `organisationSetupIssues` is the one that knows the first user
    // must be an administrator; the schema is the one that knows what an NZBN looks like.
    const issues = organisationSetupIssues({ name, adminName, roles });
    if (issues.length > 0) {
      // All of them, not the first: a notice has room for the list, and fixing one to be told the
      // next is three round trips where one would do.
      notifyProblems('Could not set that agency up', issues, SETUP_PROBLEM);
      return;
    }

    const parsed = createOrganisationSchema.safeParse({
      name,
      nzbn: nzbn.trim() === '' ? null : nzbn.trim(),
      adminName,
      adminEmail,
      adminRoles: roles,
    });
    if (!parsed.success) {
      notifyProblems(
        'Could not set that agency up',
        parsed.error.issues.map((problem) => problem.message),
        SETUP_PROBLEM,
      );
      return;
    }

    dismiss(SETUP_PROBLEM);
    create.mutate(parsed.data);
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title="New agency" subtitle="Platform administration" />
      <SettingsNav />

      <Panel className="mb-4 border-l-4 border-l-warn p-5">
        <p className="text-[0.8125rem] leading-relaxed">
          <span className="font-bold">This gives you no access to the agency you create.</span> You
          will not be a member of it. Once it exists, getting in means requesting access with a
          reason — which that agency reads in its own audit log.
        </p>
      </Panel>

      <form onChange={unsaved.touch} onSubmit={submit} noValidate>
        <Panel className="mb-4">
          <PanelHeader
            title="The agency"
            subtitle="What its own staff see, and what its owners read on every statement."
          />
          <div className="grid gap-4 border-t border-rule p-5 md:grid-cols-2">
            <Field label="Agency name" required>
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    dismiss(SETUP_PROBLEM);
                  }}
                  placeholder="Kowhai Property Management"
                  autoComplete="off"
                />
              )}
            </Field>

            <Field
              label="NZBN"
              hint="13 digits, off the paperwork. They can add it later if you do not have it."
            >
              {(props) => (
                <Input
                  {...props}
                  value={nzbn}
                  onChange={(e) => {
                    setNzbn(e.target.value);
                    dismiss(SETUP_PROBLEM);
                  }}
                  inputMode="numeric"
                  placeholder="9429000000000"
                  autoComplete="off"
                />
              )}
            </Field>
          </div>

          <p className="border-t border-rule px-5 py-3.5 text-[0.75rem] text-ink-muted">
            Their trust account, GST registration and approval thresholds are theirs to record, on
            their own settings screens. This page does not ask for them.
          </p>
        </Panel>

        <Panel className="mb-4">
          <PanelHeader
            title="Their first user"
            subtitle="They set their own password, and invite everyone else themselves."
          />
          <div className="grid gap-4 border-t border-rule p-5 md:grid-cols-2">
            <Field label="Full name" required>
              {(props) => (
                <Input
                  {...props}
                  value={adminName}
                  onChange={(e) => {
                    setAdminName(e.target.value);
                    dismiss(SETUP_PROBLEM);
                  }}
                  autoComplete="off"
                />
              )}
            </Field>

            <Field label="Email" required hint="This is also how they sign in.">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={adminEmail}
                  onChange={(e) => {
                    setAdminEmail(e.target.value);
                    dismiss(SETUP_PROBLEM);
                  }}
                  autoComplete="off"
                />
              )}
            </Field>
          </div>

          <div className="border-t border-rule p-5">
            {/*
              The AGENCY's assignable roles, from core rather than from a list here — the same
              set an agency administrator is offered, so nothing we hand out on day one is
              something they could not have granted themselves. `support` and `platform_admin`
              are not in it, and the database refuses them here regardless.
            */}
            <RolePicker
              available={assignableRoles('agency')}
              selected={roles}
              onChange={setRoles}
            />
            <p className="mt-2 text-[0.75rem] text-ink-muted">
              Administrator is required — nobody else in the new agency can add the second person. A
              trust role as well, unless somebody else there will be recording the trust account.
            </p>
          </div>
        </Panel>

        <div className="flex justify-end gap-2">
          <Link to="/settings/organisations">
            <Button variant="secondary">Cancel</Button>
          </Link>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Setting up' : 'Set up agency'}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * The one-time link, and the last moment anybody can read it.
 *
 * Only its SHA-256 is stored, so this really is the only chance to copy it — the same contract
 * as an ordinary invitation, and said out loud rather than left to be discovered by closing the
 * page. If it goes astray there is one more chance: New sign-in link, on the list, for as long
 * as that agency has never signed in.
 *
 * `setupPath` is null when the address already had a ProManage login. That person keeps their
 * password and finds a new agency waiting at sign-in — one human, one password, two
 * memberships.
 */
function SetupResult({ created }: { created: Created }) {
  const [copied, setCopied] = useState(false);
  const link = created.setupPath ? `${window.location.origin}${created.setupPath}` : null;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar title={created.name} subtitle="Agency set up" />
      <SettingsNav />

      <Panel className="mb-4">
        <PanelHeader
          title={link ? 'Send them this link' : 'They already have a ProManage login'}
          subtitle={
            link
              ? 'It works once, expires in seven days, and cannot be shown again.'
              : `${created.adminEmail} keeps the password they have.`
          }
        />

        <div className="border-t border-rule p-5">
          {link ? (
            <>
              <p className="rounded-md bg-surface-sunk p-3 font-mono text-[0.75rem] break-all select-all">
                {link}
              </p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3"
                onClick={() => {
                  void navigator.clipboard.writeText(link).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </>
          ) : (
            <p className="text-[0.8125rem] text-ink-muted">
              They can sign in now and switch into {created.name} straight away.
            </p>
          )}
        </div>

        <p className="border-t border-rule px-5 py-3.5 text-[0.8125rem] leading-relaxed">
          <span className="font-bold">You are not a member of {created.name}.</span> Nothing on this
          screen gave you access to it. If you need to look at their books — to help them set the
          trust account up, or to answer a question — request access from the organisations list,
          and they will see the reason you give.
        </p>
      </Panel>

      <div className="flex gap-2">
        <Link to="/settings/organisations">
          <Button variant="secondary">Back to organisations</Button>
        </Link>
      </div>
    </div>
  );
}
